// Example data for the open data cards (Population, Workplaces): GET /catalog and
// GET /{dataset}/sample on the open data API (config/openData.ts), plus the CSV download link.
import { openDataUrl } from '../config/openData';

export type OpenDataset = 'population' | 'workplaces';

export interface OpenDataSource {
  name: string;
  licence: string;
  url: string;
  year: number;
}

export interface OpenDataExample {
  dataset: OpenDataset;
  year: number;
  total: number;
  rowCount: number;
  /** What the value column counts: residents or employees. */
  measure: string;
  /** The highest-value cells, best first. */
  rows: Array<{ ssbid: number; lon: number; lat: number; value: number }>;
  source: OpenDataSource;
  /** Direct link to the CSV of the latest year. */
  downloadUrl: string;
}

async function getJson<T>(path: string): Promise<T> {
  const url = openDataUrl(path);
  if (!url) throw new Error('The open data API is not configured.');
  const res = await fetch(url);
  if (!res.ok) throw new Error(`The open data API answered ${res.status}.`);
  return (await res.json()) as T;
}

/** The example for one dataset, or a rejection when there is no base URL or a call fails. */
export async function fetchOpenDataExample(dataset: OpenDataset): Promise<OpenDataExample> {
  const { products } = await getJson<{ products: Array<{ product: string; latest_year: number }> }>('/catalog');
  const entry = products.find((p) => p.product === dataset);
  if (!entry) throw new Error(`No ${dataset} in the open data catalog.`);
  const year = entry.latest_year;
  const sample = await getJson<{
    year: number;
    row_count: number;
    total: number;
    measure: string;
    rows: OpenDataExample['rows'];
    source: OpenDataSource;
  }>(`/${dataset}/sample?year=${year}`);
  return {
    dataset,
    year: sample.year,
    total: sample.total,
    rowCount: sample.row_count,
    measure: sample.measure,
    rows: sample.rows.slice(0, 5),
    source: sample.source,
    downloadUrl: openDataUrl(`/${dataset}/download?year=${sample.year}&format=csv`) as string,
  };
}
